import { type FC } from 'react';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { ProductListBreadcrumbsFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListBreadcrumbsFragmentFragmentDoc';
import {
  Breadcrumbs,
  type BreadcrumbsItemProps,
} from '@packages/modules/src/Breadcrumbs/Breadcrumbs.client';

/* GraphQL */ `
  fragment ProductListBreadcrumbsFragment on CategoryResult {
    breadcrumbsJsonLdSerialized
    breadcrumbs {
      id
      name
      pathName
    }
    summary {
      totalResultCount
    }
  }
`;

/**
 * ProductListBreadcrumbs component to render the breadcrumbs for the product list. Items on mobile are limited to 3 and the rest is
 * accessible via dots click which expands the rest. Used separator is a slash icon (default).
 * */

export type ProductListBreadcrumbsProps = {
  maskedData: FragmentType<typeof ProductListBreadcrumbsFragmentFragmentDoc>;
};

export const ProductListBreadcrumbs: FC<ProductListBreadcrumbsProps> = ({ maskedData }) => {
  const data = unmask<typeof ProductListBreadcrumbsFragmentFragmentDoc>(maskedData);

  const parentItems = data.breadcrumbs.slice(0, -1).map((item) => ({
    name: item.name,
    href: item.pathName,
  }));

  const currentItem = {
    name: `${data.breadcrumbs[data.breadcrumbs.length - 1].name} (${data.summary.totalResultCount})`,
  };

  const breadcrumbItems: BreadcrumbsItemProps = [...parentItems, currentItem];

  return (
    <>
      <Breadcrumbs items={breadcrumbItems} data-testid="product-list-breadcrumbs" />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: data.breadcrumbsJsonLdSerialized,
        }}
      />
    </>
  );
};
